import rareItemCapsJson from "../generated/rare-item-caps.json" with { type: "json" };
import { ROM_HOOKS as GENERATED_ROM_HOOKS, SYM as GENERATED_SYM, VERSION_SYMBOLS } from "../generated/symbols.js";
import { SPECIES_TABLE_BASE64, SPECIES_TABLE_DATA_SHA256, SPECIES_TABLE_FILE_SHA256 } from "../generated/species_table.js";
import type { RareItemCapRecord } from "./types.js";

export * from "./types.js";
export * from "./species-table-layout.js";
export { parseSym, romOffset, sramOffset, formatSym, type SymAddr } from "./sym.js";
export { SYMBOL_MANIFEST, HOOK_MANIFEST, KNOWN_VERSION_DIFFERENCES, type ManifestEntry, type SymbolKind } from "./symbols.manifest.js";
export { text, encodeText, decodeText, encodeTextBytes, printableBytes, TextEncodeError, TEXT_TERMINATOR } from "./text.js";
export {
  species,
  speciesByDex,
  allSpecies,
  isValidSpecies,
  moveInfo,
  allMoves,
  itemInfo,
  allItems,
  mapId,
  mapName,
  MACHINES,
  MAPS,
  NUM_SPECIES_INDICES,
} from "./data.js";
export { isLegalEncounter, isKnownGiftOrStatic, npcTrades, encounterTables, giftList } from "./encounters.js";
export { trainerPrize, maxTrainerPrize, trainerClasses, trainerParty, trainerParties } from "./trainers.js";
export { applyRomPatch, isKnownRomPatchVersion, romHooksFor, ROM_PATCH_VERSION, type RomPatchGame } from "./rom-patch.js";

/** Manifest symbols (identical in Red and Blue). Generated from pokered.sym. */
export const SYM: Record<string, { bank: number; addr: number }> = GENERATED_SYM;
/** Typed view of SYM with literal keys. */
export const SYMBOLS = GENERATED_SYM;
export { VERSION_SYMBOLS };

/** Flat WRAM/HRAM addresses for hot paths; test/symbols.test.ts checks each against SYM. */
export const WRAM = {
  wPlayerName: 0xd158,
  wPartyCount: 0xd163,
  wPartyDataStart: 0xd163,
  wMainDataStart: 0xd2f7,
  wPokedexOwned: 0xd2f7,
  wNumBagItems: 0xd31d,
  wBagItems: 0xd31e,
  wPlayerMoney: 0xd347,
  wObtainedBadges: 0xd356,
  wPlayerID: 0xd359,
  wNumBoxItems: 0xd53a,
  wBoxItems: 0xd53b,
  wCurrentBoxNum: 0xd5a0,
  wPlayTimeHours: 0xda41,
  wDayCareInUse: 0xda48,
  wBoxCount: 0xda80,
  wBoxDataStart: 0xda80,
  wMainDataEnd: 0xda80,
  wBoxDataEnd: 0xdee2,
  wSpriteDataStart: 0xc100,
  wSpriteDataEnd: 0xc300,
  wIsInBattle: 0xd057,
  wLinkState: 0xd12b,
  wJoyIgnore: 0xcd6b,
  wSimulatedJoypadStatesIndex: 0xcd38,
  wWalkCounter: 0xcfc5,
  wSaveFileStatus: 0xd088,
  hTileAnimations: 0xffd7,
} as const;

/** §11.7.1 hooks plus JoypadOverworld (00:0F4D) and SaveGameData (1C:7848). */
export const ROM_HOOKS: { name: string; bank: number; addr: number }[] = GENERATED_ROM_HOOKS.map((h) => ({ ...h }));

/** The client's single-address PC watch (§7.4.1). */
export const JOYPAD_OVERWORLD = { bank: 0, addr: 0x0f4d } as const;

const RARE = rareItemCapsJson as RareItemCapRecord[];

/** Per-generation cap of each rare item, indexed like RARE_ITEMS (§14.5.1). Regenerated from map data. */
export const RARE_ITEM_CAPS: number[] = RARE.map((r) => r.cap);

/** Full regenerated rare-item table with per-source counts. */
export function rareItemTable(): RareItemCapRecord[] {
  return structuredClone(RARE);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** The §15.4.5 SpeciesTable account data (a fresh copy of generated/species_table.bin). */
export function speciesTableBin(): Uint8Array {
  return base64ToBytes(SPECIES_TABLE_BASE64);
}

/** SHA-256 (hex) of species_table.bin and of its entry bytes (the header's data_hash). */
export const SPECIES_TABLE_SHA256 = { file: SPECIES_TABLE_FILE_SHA256, data: SPECIES_TABLE_DATA_SHA256 } as const;
